import { atom } from 'jotai';
import type { SourcesQueryParams, SourceStatus } from '../types/api';
import { sourcesService } from '../services';
import { SourceResponse, SourceFormData } from '@/types/sources';

// Core state atoms
export const sourcesAtom = atom<SourceResponse[]>([]);
export const sourcesLoadingAtom = atom(false);
export const sourcesErrorAtom = atom<string | null>(null);
export const selectedSourceAtom = atom<SourceResponse | null>(null);

// Filter atoms
export const sourcesStatusFilterAtom = atom<'all' | SourceStatus>('all');
export const sourcesTypeFilterAtom = atom<'all' | string>('all');
export const sourcesSearchQueryAtom = atom('');

// Derived atom for filtered sources
export const filteredSourcesAtom = atom(get => {
  const sources = get(sourcesAtom);
  const statusFilter = get(sourcesStatusFilterAtom);
  const typeFilter = get(sourcesTypeFilterAtom);
  const searchQuery = get(sourcesSearchQueryAtom);

  let filtered = sources;

  // Apply status filter
  if (statusFilter !== 'all') {
    filtered = filtered.filter(source => source.status === statusFilter);
  }

  // Apply type filter
  if (typeFilter !== 'all') {
    filtered = filtered.filter(source => source.sourceType === typeFilter);
  }

  // Apply search filter
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase().trim();
    filtered = filtered.filter(source => {
      const fullName = `${source.firstName} ${source.lastName}`.toLowerCase();
      const expertiseList = source.expertiseAreas
        ? source.expertiseAreas.split(',').map(e => e.trim().toLowerCase())
        : [];

      return (
        fullName.includes(query) ||
        (source.company && source.company.toLowerCase().includes(query)) ||
        (source.title && source.title.toLowerCase().includes(query)) ||
        expertiseList.some(exp => exp.includes(query))
      );
    });
  }

  return filtered;
});

// Async action to fetch sources
export const fetchSourcesActionAtom = atom(
  null,
  async (get, set, params: SourcesQueryParams = {}) => {
    try {
      set(sourcesLoadingAtom, true);
      set(sourcesErrorAtom, null);

      const searchQuery = get(sourcesSearchQueryAtom);
      const statusFilter = get(sourcesStatusFilterAtom);
      const typeFilter = get(sourcesTypeFilterAtom);

      let sources: SourceResponse[];

      // Use search if there's a query
      if (searchQuery.trim()) {
        sources = await sourcesService.searchSources(searchQuery.trim());
      } else {
        // Build filter params
        const filterParams: SourcesQueryParams = { ...params };
        if (statusFilter !== 'all') filterParams.status = statusFilter;
        if (typeFilter !== 'all' && typeFilter)
          filterParams.source_type = typeFilter as any;

        sources = await sourcesService.getSources(filterParams);
      }

      // Enhance sources with computed fields
      set(sourcesAtom, sources);

      // Set first source as selected if none is selected
      const currentSelected = get(selectedSourceAtom);
      if (!currentSelected && sources.length > 0 && sources[0]) {
        set(selectedSourceAtom, sources[0]);
      }

      return sources;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch sources';
      set(sourcesErrorAtom, errorMessage);
      console.error('Error fetching sources:', error);

      // Return empty array to prevent further errors
      return [];
    } finally {
      set(sourcesLoadingAtom, false);
    }
  }
);

// Fetch source by Id
export const fetchSourceByIdActionAtom = atom(
  null,
  async (get, set, sourceId: string) => {
    try {
      set(sourcesLoadingAtom, true);
      set(sourcesErrorAtom, null);

      const source = await sourcesService.getSource(sourceId);

      const current = get(sourcesAtom);
      const updated = current.map(s => (s.id === sourceId ? source : s));
      set(sourcesAtom, updated);

      return source;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to fetch source by ID';
      set(sourcesErrorAtom, message);
      throw error;
    } finally {
      set(sourcesLoadingAtom, false);
    }
  }
);

// Action to create a source
export const createSourceActionAtom = atom(
  null,
  async (get, set, sourceData: SourceFormData) => {
    try {
      set(sourcesLoadingAtom, true);
      set(sourcesErrorAtom, null);

      // Convert form data to API format
      const createData = {
        first_name: sourceData.firstName,
        last_name: sourceData.lastName,
        email: sourceData.email || null,
        phone: sourceData.phone || null,
        title: sourceData.title || null,
        company: sourceData.company || null,
        industry: sourceData.industry || null,
        source_type: sourceData.sourceType,
        linkedin_url: sourceData.linkedinUrl || null,
        twitter_handle: sourceData.twitterHandle || null,
        website: sourceData.website || null,
        expertise_areas: sourceData.expertiseAreas || null,
        tags: sourceData.tags || null,
        notes: sourceData.notes || null,
        preferred_contact_method: sourceData.preferredContactMethod || 'email',
        consent_given: sourceData.consentGiven,
      };

      const newSourceResponse = await sourcesService.createSource(createData);

      // Add the new source to the current list (convert to SourceWithStats format)
      const currentSources = get(sourcesAtom);

      const updatedSources = [newSourceResponse, ...currentSources];
      set(sourcesAtom, updatedSources);

      // Set the new source as selected
      set(selectedSourceAtom, newSourceResponse);
      return newSourceResponse;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create source';
      set(sourcesErrorAtom, errorMessage);
      throw error;
    } finally {
      set(sourcesLoadingAtom, false);
    }
  }
);

// Action to update a source
export const updateSourceActionAtom = atom(
  null,
  async (get, set, sourceId: string, sourceData: Partial<SourceFormData>) => {
    try {
      set(sourcesLoadingAtom, true);
      set(sourcesErrorAtom, null);

      // Convert form data to API format
      const updateData = {
        first_name: sourceData.firstName ?? null,
        last_name: sourceData.lastName ?? null,
        email: sourceData.email || null,
        phone: sourceData.phone || null,
        title: sourceData.title || null,
        company: sourceData.company || null,
        industry: sourceData.industry || null,
        source_type: sourceData.sourceType || null,
        linkedin_url: sourceData.linkedinUrl || null,
        twitter_handle: sourceData.twitterHandle || null,
        website: sourceData.website || null,
        expertise_areas: sourceData.expertiseAreas || null,
        tags: sourceData.tags || null,
        notes: sourceData.notes || null,
        preferred_contact_method: sourceData.preferredContactMethod,
        consent_given: sourceData.consentGiven ?? null,
      };

      const updatedSourceResponse = await sourcesService.updateSource(
        sourceId,
        updateData
      );

      // Update the source in the current list
      const currentSources = get(sourcesAtom);
      const updatedSources = currentSources.map(source => {
        if (source.id === sourceId) {
          // Update selected source if it's the one being updated
          const selectedSource = get(selectedSourceAtom);
          if (selectedSource?.id === sourceId) {
            set(selectedSourceAtom, updatedSourceResponse);
          }

          return updatedSourceResponse;
        }
        return source;
      });

      set(sourcesAtom, updatedSources);

      return updatedSources.find(s => s.id === sourceId);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update source';
      set(sourcesErrorAtom, errorMessage);
      throw error;
    } finally {
      set(sourcesLoadingAtom, false);
    }
  }
);

// Action to delete a source
export const deleteSourceActionAtom = atom(
  null,
  async (get, set, sourceId: string) => {
    try {
      set(sourcesLoadingAtom, true);
      set(sourcesErrorAtom, null);

      await sourcesService.deleteSource(sourceId);

      // Remove the source from the current list
      const currentSources = get(sourcesAtom);
      const updatedSources = currentSources.filter(
        source => source.id !== sourceId
      );
      set(sourcesAtom, updatedSources);

      // Update selected source if it was the deleted one
      const selectedSource = get(selectedSourceAtom);
      if (selectedSource?.id === sourceId) {
        const newSelectedSource =
          updatedSources.find(source => source.id !== sourceId) || null;
        set(selectedSourceAtom, newSelectedSource);
      }

      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete source';
      set(sourcesErrorAtom, errorMessage);
      throw error;
    } finally {
      set(sourcesLoadingAtom, false);
    }
  }
);

// Action to mark source as contacted
export const markSourceContactedActionAtom = atom(
  null,
  async (get, set, sourceId: string, contactDate: string) => {
    try {
      set(sourcesLoadingAtom, true);
      set(sourcesErrorAtom, null);

      await sourcesService.updateLastContact(sourceId, {
        contact_date: contactDate,
      });

      // Refresh the sources to get updated contact info
      await get(fetchSourcesActionAtom);

      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to update contact date';
      set(sourcesErrorAtom, errorMessage);
      throw error;
    } finally {
      set(sourcesLoadingAtom, false);
    }
  }
);

// Action to update consent status
export const updateSourceConsentActionAtom = atom(
  null,
  async (get, set, sourceId: string, consentGiven: boolean) => {
    try {
      set(sourcesLoadingAtom, true);
      set(sourcesErrorAtom, null);

      await sourcesService.updateConsent(sourceId, {
        consent_given: consentGiven,
        consent_date: consentGiven ? new Date().toISOString() : null,
      });

      // Refresh the sources to get updated consent info
      await get(fetchSourcesActionAtom);

      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to update consent status';
      set(sourcesErrorAtom, errorMessage);
      throw error;
    } finally {
      set(sourcesLoadingAtom, false);
    }
  }
);

// Clear sources error action
export const clearSourcesErrorAtom = atom(null, (_get, set) => {
  set(sourcesErrorAtom, null);
});
